import { ApolloClient, InMemoryCache, split } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';

import { loadState } from './lib/sessionStorage';
import config from './config';

import fragmentTypes from './fragment-types.json';

// The shourtcat for current running protocol
const PROTOCOL = document.location.protocol;

console.log('[Apollo] Connecting to GraphQL server:', `${PROTOCOL}//${config.graphqlServer}/graphql`);

// Provide authentication token
const middlewareLink = setContext((request, previousContext) => {
    const user = loadState('user');

    const headers = { frontend: 'PANEL' };
    if (user) {
        headers.authorization = `Bearer ${user.token}`;
        headers.role = user.role;
    }

    return { headers };
});

// Setup custom network interface using URI provided in
// configuration file
const httpLink = createUploadLink({
    uri: `${PROTOCOL}//${config.graphqlServer}/graphql`,
});

// Setup web-socket connection to receive updates in real time
const wsLink = new WebSocketLink({
    uri: `${PROTOCOL === 'https:' ? 'wss:' : 'ws:'}//${config.graphqlServer}/subscriptions`,
    options: {
        reconnect: true,
        lazy: true,
    },
});

// Handle GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            );
        });
    }

    if (networkError) {
        console.error('[Network error]', {
            message: networkError.message,
            statusCode: networkError.statusCode,
            result: networkError.result,
            response: networkError.response,
        });
    }
});

// Setup link based on operation type
const link = split(
    // split based on operation type
    ({ query }) => {
        const { kind, operation } = getMainDefinition(query);
        return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    middlewareLink.concat(errorLink).concat(httpLink),
);

// Setup possibleTypes for cache
const possibleTypes = {};
if (fragmentTypes && fragmentTypes.__schema && fragmentTypes.__schema.types) {
    fragmentTypes.__schema.types.forEach(supertype => {
        if (supertype.possibleTypes) {
            possibleTypes[supertype.name] = supertype.possibleTypes.map(
                subtype => subtype.name
            );
        }
    });
}

// Setup in-memory caching
const cache = new InMemoryCache({
    possibleTypes,
    dataIdFromObject: ({ id, __typename }) =>
        id ? `${__typename}_${id}` : undefined,
});

const client = new ApolloClient({
    link,
    cache,
});

export default client;
